import React, { useRef, useEffect, useState } from 'react'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { Button, Tooltip } from '@mui/material'
import Lottie from 'lottie-react'
import fullScreen from '../assets/animations/fullScreen.json'
import { FULLSCREEN_TOGGGLE_TEXT, changeLottieColor } from '../utils/Constants'
import { makeStyles } from '../hooks/makeStyles'

const useStyles = makeStyles(() => ({
  fullScreenToggleButton: {
    background: 'none',
    // zIndex: 1100,
    transition: 'transform 0.1s',
    '&:hover': {
      transform: 'scale(1.5)',
      background: 'none',
    },
  },
  zooming: {
    animation: '$zoomAnimation 0.5s infinite',
  },
  '@keyframes zoomAnimation': {
    '0%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.2)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
  lottieAnimation: {
    width: '35px',
    height: '35px',
  },
}))

const FullScreenButton: React.FC = () => {
  const classes = useStyles()
  const isFullScreenRef = useRef<boolean>(false)
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false)

  // const black = [0, 0, 0, 1] // #000000
  // const white = [255, 255, 255, 1]
  const lightGray = [248, 249, 250, 1]

  // const blackAnimation = changeLottieColor(fullScreen, black)
  // const whiteAnimation = changeLottieColor(fullScreen, white)
  const lightGrayAnimation = changeLottieColor(fullScreen, lightGray)

  const toolTipText = isFullScreen
    ? FULLSCREEN_TOGGGLE_TEXT?.EXIT_FULL_SCREEN
    : FULLSCREEN_TOGGGLE_TEXT?.FULL_SCREEN

  const updateFullScreenStatus = () => {
    const isFullScreenActive = !!(
      document.fullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).msFullscreenElement ||
      (window.innerHeight === window.screen.height &&
        window.innerWidth === window.screen.width)
    )
    isFullScreenRef.current = isFullScreenActive
    setIsFullScreen(isFullScreenActive)
  }

  useEffect(() => {
    updateFullScreenStatus()

    const events = [
      'fullscreenchange',
      'mozfullscreenchange',
      'webkitfullscreenchange',
      'msfullscreenchange',
      'resize',
    ]

    events.forEach((event) => {
      if (event === 'resize') {
        window.addEventListener(event, updateFullScreenStatus)
      } else {
        document.addEventListener(event, updateFullScreenStatus)
      }
    })

    return () => {
      events.forEach((event) => {
        if (event === 'resize') {
          window.removeEventListener(event, updateFullScreenStatus)
        } else {
          document.removeEventListener(event, updateFullScreenStatus)
        }
      })
    }
  }, [])

  const toggleFullScreen = () => {
    if (!isFullScreenRef.current) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen()
      } else if ((document.documentElement as any).mozRequestFullScreen) {
        ;(document.documentElement as any).mozRequestFullScreen()
      } else if ((document.documentElement as any).webkitRequestFullscreen) {
        ;(document.documentElement as any).webkitRequestFullscreen()
      } else if ((document.documentElement as any).msRequestFullscreen) {
        ;(document.documentElement as any).msRequestFullscreen()
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen()
      } else if ((document as any).mozCancelFullScreen) {
        ;(document as any).mozCancelFullScreen()
      } else if ((document as any).webkitExitFullscreen) {
        ;(document as any).webkitExitFullscreen()
      } else if ((document as any).msExitFullscreen) {
        ;(document as any).msExitFullscreen()
      }
    }
  }

  return (
    <Tooltip data-testid='tooltip' title={toolTipText}>
      <Button
        color='inherit'
        size='small'
        onClick={(e) => {
          e.preventDefault()
          toggleFullScreen()
        }}
        className={`${classes?.fullScreenToggleButton} ${
          !isFullScreen ? classes?.zooming : ''
        }`}
      >
        {isFullScreen ? (
          <FullscreenExitIcon htmlColor='#ffffff' fontSize='medium' />
        ) : (
          <Lottie
            animationData={lightGrayAnimation}
            loop
            className={classes?.lottieAnimation}
          />
        )}
      </Button>
    </Tooltip>
  )
}

export default FullScreenButton
